import "./excavationPermitButtons.css";
﻿import PageBackTitle from "../Component/PageBackTitle";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Alert, Button, CircularProgress, Snackbar } from "@mui/material";
import { ArrowLeft, ArrowRight, BriefcaseBusiness, Building2, CalendarDays, CheckCircle2, ChevronDown, Clock3, Cog, Download, Eye, FileText, MessageSquare, Paperclip, ShieldCheck, UserRound, Wrench, XCircle, Zap } from "lucide-react";
import API_URL from "../Config";
import { excavationInputValue, formatExcavationDate } from "./excavationViewData";
import { fetchDepartmentAttachment, viewDepartmentAttachment } from "./departmentAttachmentPreview";
import "./departmentApprovals.css";

const normalizeDepartmentApprovals = data => {
  const approvals = Array.isArray(data?.department_approvals) ? data.department_approvals
    : Array.isArray(data?.approvals) ? data.approvals : Array.isArray(data) ? data : [];
  return approvals.map(approval => ({
    ...approval,
    id: approval.id || approval.approval_id,
    department_name: approval.department_name || approval.departmentName || approval.DepartmentName || "Department",
    approval_status: approval.approval_status || approval.status || approval.Status || "Pending",
    observations: approval.observations || "",
    remarks: approval.remarks || "",
    approved_at: approval.approved_at || approval.approvedAt || "",
    approver_name: approval.approver_name || approval.approverName || "",
    approver_designation: approval.approver_designation || approval.approverDesignation || "",
    approval_order: approval.approval_order ?? approval.approvalOrder ?? "",
    is_parallel: approval.is_parallel ?? approval.isParallel ?? 0,
    inputs: Array.isArray(approval.inputs) ? approval.inputs : Array.isArray(approval.department_inputs) ? approval.department_inputs : [],
    attachments: Array.isArray(approval.attachments) ? approval.attachments : Array.isArray(approval.files) ? approval.files : [],
  })).sort((a, b) => (Number(a.approval_order) || 0) - (Number(b.approval_order) || 0));
};
const text = value => value === null || value === undefined || value === "" ? "—" : String(value);
const reviewState = status => status === "Approved" ? "approved" : status === "Rejected" ? "rejected" : "pending";
const StatusIcon = ({ status, size = 15 }) => status === "Approved" ? <CheckCircle2 size={size} aria-hidden="true" /> : status === "Rejected" ? <XCircle size={size} aria-hidden="true" /> : <Clock3 size={size} aria-hidden="true" />;
const DepartmentIcon = ({ name }) => {
  const value = String(name).toLowerCase();
  const Icon = /electri/.test(value) ? Zap : /instrument|\bit\b/.test(value) ? Cog : /mechanic/.test(value) ? Wrench : /safety|fire/.test(value) ? ShieldCheck : Building2;
  return <Icon size={23} aria-hidden="true" />;
};
const findingLayout = inputs => {
  const isMeta = input => /^(signature|date|name\s*&\s*designation)$/i.test(input.field_name || "");
  const findings = inputs.filter(input => !isMeta(input));
  const meta = inputs.filter(isMeta).sort((a, b) => {
    const order = name => /^signature$/i.test(name) ? 0 : /^date$/i.test(name) ? 2 : 1;
    return order(a.field_name) - order(b.field_name);
  });
  return [...findings.slice(0, 1), ...meta, ...findings.slice(1)].map(input => ({
    input, wide: findings.indexOf(input) > 0 && input.field_type !== "boolean",
  }));
};
const fileSize = value => {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  return bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(0.1, bytes / 1024).toFixed(1)} KB`;
};

function IssuerAllDepartmentForms({ permit_id, permit_type_id }) {
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [reload, setReload] = useState(0);
  const [fileAction, setFileAction] = useState(null);
  const [notice, setNotice] = useState(null);
  const fileBusy = useRef(false);

  useEffect(() => {
    let active = true;
    setDepartments([]);
    setLoadError("");
    if (!permit_id) { setLoading(false); return; }
    setLoading(true);
    axios.get(`${API_URL}/api/permit/department/approvals/${permit_id}`, {
      headers: { Authorization: localStorage.getItem("authKey") },
    }).then(response => {
      if (active) setDepartments(normalizeDepartmentApprovals(response.data));
    }).catch(error => {
      if (active) setLoadError(error.response?.data?.message || "Department approvals could not be loaded. Please try again.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [permit_id, reload]);

  const handleFile = async (file, action) => {
    if (fileBusy.current) return;
    fileBusy.current = true;
    setFileAction({ id: file.id, action });
    try {
      if (action === "view") await viewDepartmentAttachment(file);
      else {
        const blob = await fetchDepartmentAttachment(file);
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = file.original_filename || "attachment";
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
    } catch (error) {
      setNotice({ severity: "error", message: error.response?.data?.message || (action === "view" && error.message?.includes("pop-ups") ? error.message : `Unable to ${action} the document. Please try again.`) });
    } finally {
      fileBusy.current = false;
      setFileAction(null);
    }
  };
  const approved = departments.filter(dept => dept.approval_status === "Approved").length;
  const rejected = departments.filter(dept => dept.approval_status === "Rejected").length;
  const pending = departments.length - approved - rejected;

  return <div className="data-form-content department-approvals-page">
    <div className="data-form-header"><div className="data-form-header-content">
      <PageBackTitle className="data-form-heading-content-left-side">Department Approvals</PageBackTitle>
    </div></div>
    <div className="header-separator" />
    <div className="data-form-body">
      {!loading && !loadError && departments.length > 0 && <div className="dept-approvals-overview">
        <div className="dept-approvals-counts" aria-label="Approval summary">
        <span className="dept-status approved"><CheckCircle2 size={16} aria-hidden="true" />{approved} approved</span>
        <span className="dept-status pending"><Clock3 size={16} aria-hidden="true" />{pending} pending</span>
        <span className="dept-status rejected"><XCircle size={16} aria-hidden="true" />{rejected} rejected</span>
        </div>
        <div className="dept-approvals-progress"><strong>{approved}<span> of {departments.length} approved</span></strong><progress value={approved} max={departments.length} aria-label="Department approval progress" /></div>
      </div>}
      {loading ? <div className="dept-approvals-empty" role="status"><CircularProgress size={28} /><p>Loading department approvals…</p></div>
        : loadError ? <Alert severity="error" action={<Button color="inherit" onClick={() => setReload(value => value + 1)}>Retry</Button>}>{loadError}</Alert>
        : !departments.length ? <div className="dept-approvals-empty"><Building2 size={34} aria-hidden="true" /><h3>No department approvals yet</h3><p>Department decisions and uploaded documents will appear here when available.</p></div>
        : <div className="dept-approvals-list">{departments.map((dept, index) => <details className="dept-approval-card" open={index === 0} key={dept.id || dept.department_id || index}>
          <summary>
            <div className="dept-approval-name"><span className="dept-approval-order">{dept.approval_order || index + 1}</span><span className="dept-approval-department-icon"><DepartmentIcon name={dept.department_name} /></span><div><h3>{dept.department_name}</h3><span>{[true, 1, "1"].includes(dept.is_parallel) ? "Parallel review" : "Sequential review"}{dept.attachments.length > 0 ? ` · ${dept.attachments.length} ${dept.attachments.length === 1 ? "document" : "documents"}` : ""}</span></div></div>
            <div className="dept-approval-summary-actions"><span className={`dept-status ${reviewState(dept.approval_status)}`}><StatusIcon status={dept.approval_status} />{dept.approval_status}</span><ChevronDown className="dept-approval-chevron" size={18} aria-hidden="true" /></div>
          </summary>
          <div className="dept-approval-body">
            <dl className="dept-reviewer-details"><div><dt><UserRound size={19} aria-hidden="true" />Reviewed by</dt><dd>{text(dept.approver_name)}</dd></div><div><dt><BriefcaseBusiness size={19} aria-hidden="true" />Designation</dt><dd>{text(dept.approver_designation)}</dd></div><div><dt><CalendarDays size={19} aria-hidden="true" />Decision date (IST)</dt><dd>{formatExcavationDate(dept.approved_at)}</dd></div></dl>
            <div className="dept-approval-content">
            {dept.inputs.length > 0 && <section className="dept-approval-findings"><h4><FileText size={17} aria-hidden="true" />Findings & precautions</h4><dl className="dept-approval-fields">{findingLayout(dept.inputs).map(({ input, wide }, fieldIndex) => <div className={wide ? "dept-approval-field-wide" : ""} key={input.id || `${input.field_name}:${fieldIndex}`}><dt>{input.field_name === "excavation_type" ? "Excavation method" : input.field_name}</dt><dd className={input.field_type === "boolean" ? "dept-approval-boolean" : ""}>{excavationInputValue(input)}</dd></div>)}</dl></section>}
            {(dept.observations || dept.remarks) && <div className="dept-approval-notes">{[["Observations", dept.observations], ["Remarks", dept.remarks]].filter(([, value]) => String(value).trim()).map(([label, value]) => <section key={label}><h4>{label === "Observations" && <MessageSquare size={18} aria-hidden="true" />}{label}</h4><p>{value}</p></section>)}</div>}
            {dept.attachments.length > 0 && <section className="dept-approval-documents"><h4><Paperclip size={16} aria-hidden="true" />Supporting documents <span>{dept.attachments.length}</span></h4><ul>{dept.attachments.map((file, fileIndex) => <li key={file.id || fileIndex}>
              <span className={`dept-document-icon${/pdf/i.test(file.file_type || file.original_filename || "") ? " is-pdf" : ""}`}><FileText size={25} aria-hidden="true" />{/pdf/i.test(file.file_type || file.original_filename || "") && <small>PDF</small>}</span>
              <div className="dept-document-info"><strong>{file.original_filename || "Uploaded document"}</strong><span>{[String(file.file_type || "Document").toUpperCase(), fileSize(file.file_size)].filter(Boolean).join(" · ")}</span></div>
              <div className="dept-document-actions">
                <Button className="excavation-permit-button excavation-permit-button--view" variant="outlined" size="small" onClick={() => handleFile(file, "view")} disabled={Boolean(fileAction) || !file.id} aria-label={`View ${file.original_filename || "document"} (opens in new tab)`} startIcon={fileAction?.id === file.id && fileAction.action === "view" ? <CircularProgress size={15} /> : <Eye size={17} />}>{fileAction?.id === file.id && fileAction.action === "view" ? "Opening…" : "View"}</Button>
                <Button variant="outlined" size="small" onClick={() => handleFile(file, "download")} disabled={Boolean(fileAction) || !file.id} aria-label={`Download ${file.original_filename || "document"}`} startIcon={fileAction?.id === file.id && fileAction.action === "download" ? <CircularProgress size={15} /> : <Download size={15} />}>Download</Button>
              </div>
            </li>)}</ul></section>}
            </div>
          </div>
        </details>)}</div>}
    </div>
    <div className="footer"><div className="button-group">
      <Button variant="contained" onClick={() => navigate(`/cold-work-permit-form/${permit_id}/${permit_type_id}?tab=Additional Precautions`)} disabled={loading} endIcon={<ArrowRight size={16} />}>Next</Button>
      <Button className="excavation-permit-button" variant="outlined" onClick={() => navigate("/cold-work-permit")} startIcon={<ArrowLeft size={16} />}>Cancel</Button>
    </div></div>
    <Snackbar open={Boolean(notice)} autoHideDuration={6000} onClose={(event, reason) => { if (reason !== "clickaway") setNotice(null); }} anchorOrigin={{ vertical: "top", horizontal: "right" }}><Alert severity={notice?.severity || "info"} onClose={() => setNotice(null)}>{notice?.message}</Alert></Snackbar>
  </div>;
}

export default IssuerAllDepartmentForms;

// --- tab wise editable ui without integration ---

// import React, { useState } from "react";
// import { useNavigate } from "react-router-dom";
// import {
//   Box,
//   Card,
//   CardContent,
//   Divider,
//   Typography,
//   Tabs,
//   Tab,
//   Paper,
//   Snackbar,
//   CircularProgress,
//   Button,
// } from "@mui/material";
// import MuiAlert from "@mui/material/Alert";
// import { IoArrowBackCircle } from "react-icons/io5";
// import axios from "axios";
// import API_URL from "../../src/Config";

// export default function IssuerAllDepartmentForms({permit_id}) {
//   const navigate = useNavigate();
//   const [activeTab, setActiveTab] = useState(0);
//   const [loadingStates, setLoadingStates] = useState({});
//   const [snackbarOpen, setSnackbarOpen] = useState(false);
//   const [snackbarMessage, setSnackbarMessage] = useState("");
//   const [severity, setSeverity] = useState("success");

//   // Form states for each department
//   const [formData, setFormData] = useState({
//     electrical: { fields: {}, file: null, remarks: "" },
//     instrument: { fields: {}, file: null, remarks: "" },
//     mechanical: { fields: {}, file: null, remarks: "" },
//     fireSafety: { fields: {}, file: null, remarks: "" },
//   });

//   const handleSnackbarClose = () => setSnackbarOpen(false);

//   const showSnackbar = (msg, type = "success") => {
//     setSnackbarMessage(msg);
//     setSeverity(type);
//     setSnackbarOpen(true);
//   };

//   const handleCancelClick = () => navigate("/cold-work-permit");

//   const handleFieldChange = (deptKey, fieldName, value) => {
//     setFormData((prev) => ({
//       ...prev,
//       [deptKey]: {
//         ...prev[deptKey],
//         fields: { ...prev[deptKey].fields, [fieldName]: value },
//       },
//     }));
//   };

//   const handleFileChange = (deptKey, file) => {
//     setFormData((prev) => ({
//       ...prev,
//       [deptKey]: { ...prev[deptKey], file },
//     }));
//   };

//   const handleRemarksChange = (deptKey, value) => {
//     setFormData((prev) => ({
//       ...prev,
//       [deptKey]: { ...prev[deptKey], remarks: value },
//     }));
//   };

//   const handleCheckboxChange = (deptKey, fieldName, checked) => {
//     setFormData((prev) => ({
//       ...prev,
//       [deptKey]: {
//         ...prev[deptKey],
//         fields: { ...prev[deptKey].fields, [fieldName]: checked },
//       },
//     }));
//   };

//   const handleSaveDepartment = async (deptKey, departmentId, departmentName) => {
//     setLoadingStates((prev) => ({ ...prev, [deptKey]: true }));

//     try {
//       const deptData = formData[deptKey];

//       // Prepare inputs array from form fields
//       const inputs = Object.entries(deptData.fields).map(([fieldName, fieldValue]) => ({
//         field_name: fieldName,
//         field_value: typeof fieldValue === "boolean" ? (fieldValue ? "true" : "false") : String(fieldValue),
//         field_type: typeof fieldValue === "boolean" ? "checkbox" : "text",
//       }));

//       // Add file if present (you may need to handle file upload separately)
//       if (deptData.file) {
//         inputs.push({
//           field_name: "uploaded_file",
//           field_value: deptData.file.name, // In production, upload file first and store URL/path
//           field_type: "file",
//         });
//       }

//       const payload = {
//         permit_id: permit_id, // Replace with actual permit ID from props/context
//         department_id: departmentId,
//         observations: deptData.remarks,
//         inputs,
//       };

//       // const authToken = localStorage.getItem("authToken"); // Adjust based on your auth implementation
//       const authKey = localStorage.getItem("authKey");
//       // const response = await axios.post(
//       //   "/api/permit/department/observations",
//       //   payload,
//       //   {
//       //     headers: {
//       //       Authorization: authToken,
//       //       "Content-Type": "application/json",
//       //     },
//       //   }
//       // );

//       const response = await axios.post(
//         `${API_URL}/api/permit/department/observations`,
//         payload,
//         {
//           headers: {
//             Authorization: authKey,
//             "Content-Type": "application/json",
//           },
//         }
//       );

// if (response.status === 200) {
//       showSnackbar(`${departmentName} data saved successfully!`, "success");

//       }
//     } catch (error) {
//       console.error("Error saving department:", error);
//       showSnackbar(
//         error.response?.data?.message || "Failed to save department data",
//         "error"
//       );
//     } finally {
//       setLoadingStates((prev) => ({ ...prev, [deptKey]: false }));
//     }
//   };

//   // Input Components with state management
//   const TextInput = ({ label, deptKey, fieldName, type = "text" }) => (
//     <Box sx={{ mb: 2.5 }}>
//       <Typography
//         variant="subtitle2"
//         sx={{ mb: 0.8, fontWeight: 600, color: "text.primary" }}
//       >
//         {label}
//       </Typography>
//       <input
//         type={type}
//         value={formData[deptKey]?.fields[fieldName] || ""}
//         onChange={(e) => handleFieldChange(deptKey, fieldName, e.target.value)}
//         style={{
//           width: "100%",
//           padding: "10px 12px",
//           borderRadius: "6px",
//           border: "1px solid #d0d0d0",
//           fontSize: "14px",
//         }}
//       />
//     </Box>
//   );

//   const TextArea = ({ label, deptKey, fieldName, rows = 3 }) => (
//     <Box sx={{ mb: 2.5 }}>
//       <Typography
//         variant="subtitle2"
//         sx={{ mb: 0.8, fontWeight: 600, color: "text.primary" }}
//       >
//         {label}
//       </Typography>
//       <textarea
//         rows={rows}
//         value={formData[deptKey]?.fields[fieldName] || ""}
//         onChange={(e) => handleFieldChange(deptKey, fieldName, e.target.value)}
//         style={{
//           width: "100%",
//           padding: "10px 12px",
//           borderRadius: "6px",
//           border: "1px solid #d0d0d0",
//           fontSize: "14px",
//           fontFamily: "inherit",
//           resize: "vertical",
//         }}
//       />
//     </Box>
//   );

//   const Checkbox = ({ label, deptKey, fieldName }) => (
//     <Box
//       sx={{
//         display: "inline-flex",
//         alignItems: "center",
//         mr: 2,
//         mb: 1,
//         p: 1,
//         borderRadius: 1,
//       }}
//     >
//       <input
//         type="checkbox"
//         checked={formData[deptKey]?.fields[fieldName] || false}
//         onChange={(e) => handleCheckboxChange(deptKey, fieldName, e.target.checked)}
//         style={{
//           marginRight: "8px",
//           width: "18px",
//           height: "18px",
//           cursor: "pointer",
//         }}
//       />
//       <Typography variant="body2">{label}</Typography>
//     </Box>
//   );

//   const FileUpload = ({ deptKey }) => (
//     <Box sx={{ mb: 2.5 }}>
//       <Typography
//         variant="subtitle2"
//         sx={{ mb: 0.8, fontWeight: 600, color: "text.primary" }}
//       >
//         Upload File
//       </Typography>
//       <input
//         type="file"
//         onChange={(e) => handleFileChange(deptKey, e.target.files[0])}
//         style={{
//           width: "100%",
//           padding: "10px 12px",
//           borderRadius: "6px",
//           border: "1px solid #d0d0d0",
//           fontSize: "14px",
//         }}
//       />
//       {formData[deptKey]?.file && (
//         <Typography variant="caption" sx={{ mt: 0.5, display: "block", color: "text.secondary" }}>
//           Selected: {formData[deptKey].file.name}
//         </Typography>
//       )}
//     </Box>
//   );

//   const RemarksField = ({ deptKey }) => (
//     <Box sx={{ mb: 2.5 }}>
//       <Typography
//         variant="subtitle2"
//         sx={{ mb: 0.8, fontWeight: 600, color: "text.primary" }}
//       >
//         Remarks
//       </Typography>
//       <textarea
//         rows={3}
//         value={formData[deptKey]?.remarks || ""}
//         onChange={(e) => handleRemarksChange(deptKey, e.target.value)}
//         placeholder="Enter any additional remarks or observations..."
//         style={{
//           width: "100%",
//           padding: "10px 12px",
//           borderRadius: "6px",
//           border: "1px solid #d0d0d0",
//           fontSize: "14px",
//           fontFamily: "inherit",
//           resize: "vertical",
//         }}
//       />
//     </Box>
//   );

//   const SaveButton = ({ deptKey, departmentId, departmentName }) => (
//     <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
//       <Button
//         variant="contained"
//         onClick={() => handleSaveDepartment(deptKey, departmentId, departmentName)}
//         disabled={loadingStates[deptKey]}
//         sx={{
//           bgcolor: "#233b80",
//           "&:hover": { bgcolor: "#1a2d66" },
//           textTransform: "none",
//           px: 4,
//           py: 1.2,
//         }}
//       >
//         {loadingStates[deptKey] ? (
//           <CircularProgress size={20} color="inherit" />
//         ) : (
//           "Save Department"
//         )}
//       </Button>
//     </Box>
//   );

//   const departments = [
//     { name: "Electrical Department", key: "electrical", id: "4241c348-f2ae-48b6-8267-f8ba62c1f5a7" },
//     { name: "Instrument / IT Department", key: "instrument", id: "dept_002" },
//     { name: "Mechanical Department", key: "mechanical", id: "f4dadd39-6761-474d-88e3-26b79a4d9f8c" },
//     { name: "Fire & Safety Department", key: "fireSafety", id: "d0164b08-5bcf-4f85-98fb-4e62cd031b3d" },
//   ];

//   const renderDepartmentForm = (dept) => {
//     switch (dept.name) {
//       case "Electrical Department":
//         return (
//           <Box>
//             <Typography
//               variant="h6"
//               sx={{
//                 mb: 2,
//                 fontWeight: 600,
//                 color: "primary.main",
//                 borderBottom: "2px solid",
//                 borderColor: "primary.light",
//                 pb: 1,
//               }}
//             >
//               Electrical Department
//             </Typography>
//             <TextArea
//               label="Details of underground cables present in the area to be excavated"
//               deptKey={dept.key}
//               fieldName="underground_cables"
//               rows={4}
//             />
//             <TextArea
//               label="Electrical isolation required"
//               deptKey={dept.key}
//               fieldName="electrical_isolation"
//               rows={4}
//             />
//             <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2 }}>
//               <TextInput label="Date" deptKey={dept.key} fieldName="date" type="date" />
//               <TextInput label="Name & Designation" deptKey={dept.key} fieldName="name_designation" />
//               <TextInput label="Signature" deptKey={dept.key} fieldName="signature" />
//             </Box>
//             <FileUpload deptKey={dept.key} />
//             <RemarksField deptKey={dept.key} />
//             <SaveButton deptKey={dept.key} departmentId={dept.id} departmentName={dept.name} />
//           </Box>
//         );

//       case "Instrument / IT Department":
//         return (
//           <Box>
//             <Typography
//               variant="h6"
//               sx={{
//                 mb: 2,
//                 fontWeight: 600,
//                 color: "primary.main",
//                 borderBottom: "2px solid",
//                 borderColor: "primary.light",
//                 pb: 1,
//               }}
//             >
//               Instrument / IT Department
//             </Typography>
//             <TextArea
//               label="Details of underground cables present in the area to be excavated"
//               deptKey={dept.key}
//               fieldName="underground_cables"
//               rows={4}
//             />
//             <TextArea
//               label="Precautions to be observed for safe excavation job"
//               deptKey={dept.key}
//               fieldName="precautions"
//               rows={4}
//             />
//             <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2 }}>
//               <TextInput label="Date" deptKey={dept.key} fieldName="date" type="date" />
//               <TextInput label="Name & Designation" deptKey={dept.key} fieldName="name_designation" />
//               <TextInput label="Signature" deptKey={dept.key} fieldName="signature" />
//             </Box>
//             <FileUpload deptKey={dept.key} />
//             <RemarksField deptKey={dept.key} />
//             <SaveButton deptKey={dept.key} departmentId={dept.id} departmentName={dept.name} />
//           </Box>
//         );

//       case "Mechanical Department":
//         return (
//           <Box>
//             <Typography
//               variant="h6"
//               sx={{
//                 mb: 2,
//                 fontWeight: 600,
//                 color: "primary.main",
//                 borderBottom: "2px solid",
//                 borderColor: "primary.light",
//                 pb: 1,
//               }}
//             >
//               Mechanical Department
//             </Typography>
//             <TextArea
//               label="Description of underground facilities existing at the proposed location"
//               deptKey={dept.key}
//               fieldName="underground_facilities"
//               rows={4}
//             />
//             <TextArea
//               label="Precautions to be observed for safe excavation job"
//               deptKey={dept.key}
//               fieldName="precautions"
//               rows={4}
//             />
//             <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2 }}>
//               <TextInput label="Date" deptKey={dept.key} fieldName="date" type="date" />
//               <TextInput label="Name & Designation" deptKey={dept.key} fieldName="name_designation" />
//               <TextInput label="Signature" deptKey={dept.key} fieldName="signature" />
//             </Box>
//             <FileUpload deptKey={dept.key} />
//             <RemarksField deptKey={dept.key} />
//             <SaveButton deptKey={dept.key} departmentId={dept.id} departmentName={dept.name} />
//           </Box>
//         );

//       case "Fire & Safety Department":
//         return (
//           <Box>
//             <Typography
//               variant="h6"
//               sx={{
//                 mb: 2,
//                 fontWeight: 600,
//                 color: "primary.main",
//                 borderBottom: "2px solid",
//                 borderColor: "primary.light",
//                 pb: 1,
//               }}
//             >
//               Fire & Safety Department
//             </Typography>

//             <Paper sx={{ p: 2, mb: 2, bgcolor: "grey.50" }}>
//               <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
//                 Provide:
//               </Typography>
//               <Box sx={{ display: "flex", flexWrap: "wrap" }}>
//                 <Checkbox label="Cross over" deptKey={dept.key} fieldName="crossover" />
//                 <Checkbox label="Barricades" deptKey={dept.key} fieldName="barricades" />
//                 <Checkbox label="Road blocks" deptKey={dept.key} fieldName="road_blocks" />
//                 <Checkbox label="Shoring" deptKey={dept.key} fieldName="shoring" />
//               </Box>
//             </Paper>

//             <TextInput label="Others (if any)" deptKey={dept.key} fieldName="others_provide" />
//             <TextInput label="Types of Excavation" deptKey={dept.key} fieldName="excavation_type" />

//             <Paper sx={{ p: 2, mb: 2, bgcolor: "grey.50" }}>
//               <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
//                 Use following safety equipment:
//               </Typography>
//               <Box sx={{ display: "flex", flexWrap: "wrap" }}>
//                 <Checkbox label="Gas Masks" deptKey={dept.key} fieldName="gas_masks" />
//                 <Checkbox label="Eye Protection" deptKey={dept.key} fieldName="eye_protection" />
//                 <Checkbox label="Ear Protection" deptKey={dept.key} fieldName="ear_protection" />
//                 <Checkbox label="Wooden Handheld tools" deptKey={dept.key} fieldName="wooden_tools" />
//               </Box>
//             </Paper>

//             <TextInput label="Other safety equipment" deptKey={dept.key} fieldName="other_safety_equipment" />

//             <Paper sx={{ p: 2, mb: 2, bgcolor: "grey.50" }}>
//               <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
//                 Emergency Approach:
//               </Typography>
//               <Box sx={{ display: "flex", flexWrap: "wrap" }}>
//                 <Checkbox label="Emergency approach for vehicles" deptKey={dept.key} fieldName="emergency_vehicles" />
//                 <Checkbox label="Barricading" deptKey={dept.key} fieldName="emergency_barricading" />
//                 <Checkbox label="Road closure" deptKey={dept.key} fieldName="road_closure" />
//               </Box>
//             </Paper>

//             <TextArea label="Other precautions" deptKey={dept.key} fieldName="other_precautions" rows={4} />
//             <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" }, gap: 2 }}>
//               <TextInput label="Date" deptKey={dept.key} fieldName="date" type="date" />
//               <TextInput label="Name & Designation" deptKey={dept.key} fieldName="name_designation" />
//             </Box>
//             <FileUpload deptKey={dept.key} />
//             <RemarksField deptKey={dept.key} />
//             <SaveButton deptKey={dept.key} departmentId={dept.id} departmentName={dept.name} />
//           </Box>
//         );

//       default:
//         return null;
//     }
//   };

//   return (
//     <>
//       <div className="data-form-content">
//         <div className="data-form-body-permit data-form-body-permit-excavation">
//           <div className="">
//             <IoArrowBackCircle
//               style={{ color: "#233b80", cursor: "pointer" }}
//               size={25}
//               onClick={handleCancelClick}
//             />
//             <span className="data-form-heading-text">
//               Excavation Clearance / Road Closure - All Department Forms
//             </span>
//           </div>
//           <Divider />

//           <Card sx={{ borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.06)", mt: 2 }}>
//             <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "grey.50" }}>
//               <Tabs
//                 value={activeTab}
//                 onChange={(e, newValue) => setActiveTab(newValue)}
//                 variant="scrollable"
//                 scrollButtons="auto"
//               >
//                 {departments.map((dept, i) => (
//                   <Tab key={i} label={dept.name} />
//                 ))}
//               </Tabs>
//             </Box>

//             <CardContent sx={{ p: 3 }}>
//               {departments.map((dept, i) => (
//                 <Box key={i} hidden={activeTab !== i}>
//                   {activeTab === i && renderDepartmentForm(dept)}
//                 </Box>
//               ))}
//             </CardContent>
//           </Card>
//         </div>

//         <div className="footer">
//           <div className="button-group">
//             <button className="data-form-cancel-button" onClick={handleCancelClick}>
//               Cancel
//             </button>
//           </div>
//         </div>
//       </div>

//       <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={handleSnackbarClose}>
//         <MuiAlert severity={severity} variant="filled">
//           {snackbarMessage}
//         </MuiAlert>
//       </Snackbar>
//     </>
//   );
// }
